import { PaginatorInfo } from "./generic.dto";
import { EmployeePayInfo } from "./employee-pay-info.dto";
import {
	EmployeeAccountInfo,
	EmployeeAccountInfoDto,
} from "./employee-account-info.dto";
import { JobTitleDto, JobTitleSummary } from "./job-titles.dto";
import { EmployeeContractDto } from "./employee-contract.dto";
import { CompanyGroupingDto } from "./company-grouping.dto";
import { GradeLevelDto } from "./grade-level.dto";
import { DepartmentDto } from "./department.dto";
import { CostCenterDto } from "./cost-center.dto";
import {
	CreateEmployeeIdInfoDto,
	EmployeeIdInfoDto,
	UpdateEmployeeIdInfoDto,
} from "./employee-id-info.dto";
import { QueryEmployeeEngagementDto } from "./employee-engagement.dto";

export const GENDER = {
	MALE: "MALE",
	FEMALE: "FEMALE",
};

export type GENDER = (typeof GENDER)[keyof typeof GENDER];

export const PAY_PERIOD = {
	HOUR: "HOUR",
	DAY: "DAY",
	WEEK: "WEEK",
	MONTH: "MONTH",
	QUARTER: "QUARTER",
	YEAR: "YEAR",
};

export type PAY_PERIOD = (typeof PAY_PERIOD)[keyof typeof PAY_PERIOD];

export const EMPLOYEE_PAY_INFO_TYPE = {
	WALLET: "WALLET",
	BANK: "BANK",
};

export type EMPLOYEE_PAY_INFO_TYPE =
	(typeof EMPLOYEE_PAY_INFO_TYPE)[keyof typeof EMPLOYEE_PAY_INFO_TYPE];

export const EMPLOYEE_ACCOUNT_INFO_TYPE = {
	WALLET: "WALLET",
	BANK: "BANK",
};

export type EMPLOYEE_ACCOUNT_INFO_TYPE =
	(typeof EMPLOYEE_ACCOUNT_INFO_TYPE)[keyof typeof EMPLOYEE_ACCOUNT_INFO_TYPE];

export const STATUS = {
	ACTIVE: "ACTIVE",
	ELAPSED_CONTRACT: "ELAPSED_CONTRACT",
	TERMINATED: "TERMINATED",
	DISABLED: "DISABLED",
	ARCHIVED: "ARCHIVED",
	RESIGNED: "RESIGNED",
};

export enum MaritalStatus {
	SINGLE = "SINGLE",
	MARRIED = "MARRIED",
	DIVORCED = "DIVORCED",
	WIDOWED = "WIDOWED",
}

export type STATUS = (typeof STATUS)[keyof typeof STATUS];

export class CreateEmployeePayInfoDto {
	basicPay!: number;
	payPeriod!: PAY_PERIOD;
	splitPayment!: boolean;
	primaryPayPercent!: number;
	primaryPaymentChannel!: EMPLOYEE_PAY_INFO_TYPE;
	secondaryPaymentChannel?: EMPLOYEE_PAY_INFO_TYPE;
	currencyId!: number;
}

export class UpdateEmployeePayInfoDto {
	basicPay?: number;
	payPeriod?: PAY_PERIOD;
	splitPayment?: boolean;
	primaryPayPercent?: number;
	primaryPaymentChannel?: EMPLOYEE_PAY_INFO_TYPE;
	secondaryPaymentChannel?: EMPLOYEE_PAY_INFO_TYPE;
	currencyId?: number;
}

export class EmployeeIdInfoObjectDto {
	id?: number;
	typeId?: number;
	issuingCountry?: string;
	value?: string;
}

export class CreateEmployeeAccountInfoDto {
	type!: EMPLOYEE_ACCOUNT_INFO_TYPE;
	institutionId!: number;
	institutionBranchId!: number;
	accountNumber!: string;
	primary?: boolean;
	secondary?: boolean;
	active!: boolean;
	currencyId?: number;
}

export class UpdateEmployeeAccountInfoDto {
	id!: number;
	employeeId?: number;
	type?: EMPLOYEE_ACCOUNT_INFO_TYPE;
	institutionId?: number;
	institutionBranchId?: number;
	accountNumber?: string;
	primary?: boolean;
	secondary?: boolean;
	active?: boolean;
	currencyId?: number;
}

export class CreateEmployeeDto {
	companyId!: number;
	notchId?: number;
	employeeNumber!: string;
	title?: string | null;
	firstName!: string;
	lastName!: string;
	otherNames?: string;
	gender!: GENDER;
	dateOfBirth!: string;
	photoUrl?: string;
	ssn?: string;
	taxIdentificationNumber?: string;
	majorGradeLevelId?: number;
	minorGradeLevelId?: number;
	nationality!: string;
	regionId?: number;
	tribeId?: number;
	email?: string;
	privateEmail?: string;
	msisdn?: string;
	address?: string;
	alternateMsisdn?: string;
	digitalAddress?: string;
	jobTitleId?: number;
	departmentId?: number;
	divisionId?: number;
	stationId?: number;
	costAreaId?: number;
	status!: STATUS;
	employmentDate!: string;
	terminationDate?: string;
	reemployed!: boolean;
	resident?: boolean;
	unionMember?: boolean;
	employeePayInfo?: CreateEmployeePayInfoDto;
	employeeAccountInfo?: CreateEmployeeAccountInfoDto[];
	hr?: boolean;
	username?: string;
	contract?: {
		contractTypeId: number;
		contractNumber?: string;
		startDate?: Date;
	};
	pensioner?: boolean;
	idInfo?: CreateEmployeeIdInfoDto[];
	excludeFromPayrollRun?: boolean;
	maritalStatus?: MaritalStatus;
}

export class UpdateEmployeeDto {
	notchId?: number;
	employeeNumber?: string;
	title?: string | null;
	firstName?: string;
	lastName?: string;
	otherNames?: string;
	gender?: GENDER;
	dateOfBirth?: string;
	photoUrl?: string;
	ssn?: string;
	taxIdentificationNumber?: string;
	majorGradeLevelId?: number;
	minorGradeLevelId?: number;
	nationality?: string;
	regionId?: number;
	tribeId?: number;
	email?: string;
	privateEmail?: string;
	msisdn?: string;
	address?: string;
	alternateMsisdn?: string;
	digitalAddress?: string;
	jobTitleId?: number;
	departmentId?: number;
	divisionId?: number;
	stationId?: number;
	costAreaId?: number;
	status?: STATUS;
	employmentDate?: string;
	terminationDate?: string;
	reemployed?: boolean;
	resident?: boolean;
	unionMember?: boolean;
	employeePayInfo?: UpdateEmployeePayInfoDto;
	employeeAccountInfo?: UpdateEmployeeAccountInfoDto[];
	hr?: boolean;
	pensioner?: boolean;
	idInfo?: EmployeeIdInfoObjectDto[];
	removeIdInfoIds?: number[];
	excludeFromPayrollRun?: boolean;
	maritalStatus?: MaritalStatus;
	removeDepartmentId?: number;
	removeTribeId?: number;
	removeDivisionId?: number;
	removeStationId?: number;
	removeRegionId?: number;
 	removeEmail?: boolean;
}

export interface EmployeeDto extends Omit<
	CreateEmployeeDto,
	"employeeAccountInfo" | "employeePayInfo"
> {
	id: number;
	employeePayInfo: EmployeePayInfo;
	username?: string;
	employeeAccountInfo: EmployeeAccountInfo[];
	jobTitle?: JobTitleDto | null;
	employeeContracts?: EmployeeContractDto[];
	tribe?: CompanyGroupingDto;
	division?: CompanyGroupingDto;
	station?: CompanyGroupingDto;
	region?: CompanyGroupingDto;
	majorGradeLevel?: GradeLevelDto;
	minorGradeLevel?: GradeLevelDto;
	department?: DepartmentDto;
	costCentre?: CostCenterDto;
	idInfo?: EmployeeIdInfoDto[];
}

export class UploadEmployeeViaSpreadsheetDto {
	rowNumber?: number;
	companyId!: number;
	employeeNumber!: string;
	notchCode!: string;
	firstName!: string;
	lastName!: string;
	otherNames?: string;
	title!: string;
	gender!: GENDER;
	dob!: string;
	photoUrl?: string;
	ssn?: string;
	tin?: string;
	gradeMajorLevelCode?: string;
	gradeMinorLevelCode?: string;
	nationalityCode!: string;
	regionCode!: string;
	resident!: "Yes" | "No";
	splitPayment!: "Yes" | "No";
	primaryPayPercentage!: number;
	primaryPaymentChannel!: "BANK" | "WALLET";
	primaryAccountInstitutionCode!: string;
	primaryAccountBranchSortCode!: string;
	primaryAccountNumber!: string;
	secondaryPaymentChannel!: "BANK" | "WALLET";
	secondaryAccountInstitutionCode!: string;
	secondaryAccountBranchSortCode!: string;
	secondaryAccountNumber!: string;
	emailAddress!: string;
	privateEmailAddress!: string;
	phoneNumber!: string;
	alternatePhoneNumber!: string;
	employmentDate!: Date;
	terminationDate?: Date;
	reemployed!: "Yes" | "No";
	departmentCode!: string;
	divisionCode!: string;
	stationCode!: string;
	jobTitle!: string;

	straightShiftWorker!: "Yes" | "No";
	firstCategoryLevel?: number;
	secondCategoryLevel?: number;
	thirdCategoryLevel?: number;
	fourthCategoryLevel?: number;
	fifthCategoryLevel?: number;
	currency?: string;
	basicPay!: number;
	unionMember!: "Yes" | "No";

	hr?: "Yes" | "No";
	contractTypeCode?: string;
	contractNumber?: string;
	contractStartDate?: Date;

	idTypeCode?: string;
	idIssuingCountryCode?: string;
	idNumber?: string;
}

export class QueryEmployeeDto {
	companyId?: number;
	employeeNumber?: string;
	notchId?: number;
	gender?: GENDER;
	majorGradeLevelId?: number;
	minorGradeLevelId?: number;
	jobTitleId?: number;
	status?: STATUS;
	hr?: boolean;
	"dateOfBirth.gte"?: string;
	"dateOfBirth.lte"?: string;
	"employmentDate.gte"?: string;
	"employmentDate.lte"?: string;
	"terminationDate.gte"?: string;
	"terminationDate.lte"?: string;
	"createdAt.gte"?: string;
	"createdAt.lte"?: string;
	reemployed?: boolean;
	resident?: boolean;
	unionMember?: boolean;
	userStatus?: string;
	page?: number = 1;
	limit?: number;
	orderBy?: EmployeeOrderBy;
}

export class CountEmployeeDto {
	organizationId?: string;
	companyId?: number;
	employeeBandId?: number;
	notchId?: number;
	gender?: GENDER;
	majorGradeLevelId?: number;
	minorGradeLevelId?: number;
	//  departmentCode?: string;
	jobTitleId?: number;
	status?: STATUS;
	"dateOfBirth.gte"?: string;
	"dateOfBirth.lte"?: string;
	"employmentDate.gte"?: string;
	"employmentDate.lte"?: string;
	"terminationDate.gte"?: string;
	"terminationDate.lte"?: string;
	"createdAt.gte"?: string;
	"createdAt.lte"?: string;
	reemployed?: boolean;
	resident?: boolean;
	unionMember?: boolean;
	//  stationCode?: string;
}

export interface EmployeePaginator extends PaginatorInfo<EmployeeDto> {}

export enum EmployeeOrderBy {
	CREATED_AT_ASC = "createdAt:asc",
	CREATED_AT_DESC = "createdAt:desc",
	MODIFIED_AT_ASC = "modifiedAt:asc",
	MODIFIED_AT_DESC = "modifiedAt:desc",
	FIRST_NAME_ASC = "firstName:asc",
	FIRST_NAME_DESC = "firstName:desc",
	LAST_NAME_ASC = "lastName:asc",
	LAST_NAME_DESC = "lastName:desc",
}

export class SearchEmployeeDto {
	q?: string;
	companyId?: number;
	page: number = 1;
	limit?: number;
	orderBy: EmployeeOrderBy = EmployeeOrderBy.CREATED_AT_ASC;
}

export class UploadEmployeeResponse {
	successful!: {
		employeeId?: number;
		rowNumber?: number;
		accountCreationErroMessage?: string;
		accountCreationInitiated?: boolean;
		accountOperationErroMessage?: string;
		accountOperationInitiated?: boolean;
		accountOperation?: AccountOperation;
	}[];
	failed!: {
		rowNumber?: number;
		errors: {
			column: string;
			reason: string;
		}[];
	}[];
}

export enum AccountOperation {
	ADD_LOGIN_ACCOUNT = "ADD LOGIN ACCOUNT",
	UPDATE_LOGIN_ACCOUNT = "UPDATE LOGIN ACCOUNT",
}

export class EmployeeTerminationDto {
	terminationDate!: string;
	resignation?: boolean;
	reason?: string;
}

export class EmployeeReinstatementDto extends UpdateEmployeeDto {
	reemployment!: boolean;
}

export interface QueryEmployeeNoPaginationDto extends Omit<
	QueryEmployeeDto,
	"page" | "limit"
> {}

export class QueryEmployeeMasterListDto {
	includeNonActiveEmployees?: boolean;
}

export class EmployeeMasterListReportObject {
	id!: number;
	fullName!: string;
	department?: {
		id?: number;
		name?: string;
		code?: string;
	};
	position?: {
		id?: number;
		name?: string;
		code?: string;
	};
	dateOfHire!: Date;
}
export interface EmployeeMasterListReportPaginationDto extends PaginatorInfo<EmployeeMasterListReportObject> {}

export class QueryEmployeeExitReportDto {
	"terminationDate.gte"?: Date;
	"terminationDate.lte"?: Date;
}

export class EmployeeExitReportObject {
	employee!: EmployeeDto;
	exitDate!: Date;
	exitType!: STATUS;
}
export interface EmployeeExitReportPaginationDto extends PaginatorInfo<EmployeeExitReportObject> {}

export class NewHiresReportObject {
	employee!: {
		id: number;
		name: string;
	};
	jobTitle?: JobTitleSummary;
	department?: {
		id: number;
		name: string;
		code: string;
	};
	startDate!: Date;
}
export interface NewHiresReportPaginationDto extends PaginatorInfo<NewHiresReportObject> {}

export interface QueryNewHireDto extends Omit<
	QueryEmployeeEngagementDto,
	"employeeId" | "page" | "limit"
> {}

export class ExportEmployeeQueryDto {
	employeeNumber?: boolean;
	lastName?: boolean;
	firstName?: boolean;
	notchCode?: boolean;
	otherNames?: boolean;
	gender?: boolean;
	dob?: boolean;
	photoUrl?: boolean;
	ssn?: boolean;
	tin?: boolean;
	gradeMajorLevelCode?: boolean;
	gradeMinorLevelCode?: boolean;
	nationalityCode?: boolean;
	regionCode?: boolean;
	resident?: boolean;
	splitPayment?: boolean;
	primaryPayPercentage?: boolean;
	primaryPaymentChannel?: boolean;
	primaryAccountInstitutionCode?: boolean;
	primaryAccountBranchSortCode?: boolean;
	primaryAccountNumber?: boolean;
	secondaryPaymentChannel?: boolean;
	secondaryAccountInstitutionCode?: boolean;
	secondaryAccountBranchSortCode?: boolean;
	secondaryAccountNumber?: boolean;
	emailAddress?: boolean;
	privateEmailAddress?: boolean;
	phoneNumber?: boolean;
	alternatePhoneNumber?: boolean;
	employmentDate?: boolean;
	terminationDate?: boolean;
	reemployed?: boolean;
	departmentCode?: boolean;
	divisionCode?: boolean;
	stationCode?: boolean;
	jobTitleCode?: boolean;
	currency?: boolean;
	basicPay?: boolean;
	unionMember?: boolean;
	hr?: boolean;
	contractTypeCode?: boolean;
	contractNumber?: boolean;
	contractStartDate?: boolean;
	idTypeCode?: boolean;
	idIssuingCountryCode?: boolean;
	idNumber?: boolean;
	maritalStatus?: MaritalStatus;
	orderBy: EmployeeOrderBy = EmployeeOrderBy.CREATED_AT_ASC;
}
